import { Gem } from 'lucide-react';

import { useId, useState } from 'react';

import { formatProMovableAt, isProSlotLocked } from '@splitfiesta/core';
import { useTranslation } from '@splitfiesta/i18n';
import type { ProGroupListItemDto, UserProDto } from '@splitfiesta/shared';
import { Alert, Button, Card } from '@splitfiesta/ui';

import { ConfirmationDialog } from '@/components/common/ConfirmationDialog';
import { SupporterBadge } from '@/components/pro/SupporterBadge';

export interface ProSectionProps {
  pro: UserProDto;
  /** The formatted `until`, or null for no end. */
  untilLabel: string | null;
  proGroups: ProGroupListItemDto[];
  loadingGroups: boolean;
  /** The list failed; never shown as an empty list. */
  groupsFailed: boolean;
  /** The list is being fetched again after a failure. */
  retryingGroups?: boolean;
  onRetry: () => void;
  onRemove: (groupId: string) => void;
  busyGroupId?: string;
  isOnline: boolean;
  scansIncluded?: boolean;
  /** Opens the group picker; omitted when no slot is free. */
  onMakePro?: () => void;
  /** A picked group whose dashboard the dialog is still waiting for. */
  pick?: { failed: boolean; retrying: boolean; onRetry: () => void };
}

export function ProSection({
  pro,
  untilLabel,
  proGroups,
  loadingGroups,
  groupsFailed,
  retryingGroups = false,
  onRetry,
  onRemove,
  busyGroupId,
  isOnline,
  scansIncluded = false,
  onMakePro,
  pick,
}: ProSectionProps) {
  const { t, i18n } = useTranslation();
  const [confirming, setConfirming] = useState<ProGroupListItemDto | null>(
    null
  );
  const [now] = useState(() => new Date().toISOString());
  const offlineHintId = useId();
  const offlineHint = t('groups:pro.offlineDisabled');
  const showEmpty = !loadingGroups && !groupsFailed && proGroups.length === 0;
  const makeProButton = onMakePro ? (
    <Button
      className="min-h-[44px]"
      onClick={onMakePro}
      disabled={!isOnline}
      title={isOnline ? undefined : offlineHint}
      data-testid="settings-pro-make-pro"
    >
      {t('settings:pro.makeGroupPro')}
    </Button>
  ) : null;
  const pickFeedback = !pick ? null : pick.failed ? (
    <div className="space-y-2" data-testid="settings-pro-pick-failed">
      <Alert type="error" message={t('errors:api.status.default')} />
      <Button
        variant="outline"
        size="sm"
        className="min-h-[44px]"
        onClick={pick.onRetry}
        loading={pick.retrying}
      >
        {t('common:errors.retry')}
      </Button>
    </div>
  ) : (
    <div role="status" data-testid="settings-pro-pick-loading">
      <div aria-hidden="true" className="loading-spinner w-5 h-5" />
      <span className="sr-only">{t('common:actions.loading')}</span>
    </div>
  );
  const makeProAction = makeProButton ? (
    <div className="space-y-3">
      {makeProButton}
      {pickFeedback}
    </div>
  ) : null;

  return (
    <Card data-testid="settings-pro-card">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
        <Gem
          aria-hidden="true"
          className="w-5 h-5 mr-2 text-orange-600 dark:text-orange-400"
        />
        {t('settings:pro.title')}
      </h3>

      <div className="space-y-4">
        <div className="space-y-1">
          <p
            className="text-sm font-medium text-gray-900 dark:text-gray-100"
            data-testid="settings-pro-status"
          >
            {untilLabel
              ? t('settings:pro.until', { date: untilLabel })
              : t('settings:pro.noEnd')}
          </p>
          {scansIncluded && (
            <p
              className="flex flex-wrap gap-x-3 text-sm"
              data-testid="settings-pro-scans"
            >
              <span className="min-w-[45%] flex-1 text-gray-900 dark:text-gray-100">
                {t('settings:scans.title')}
              </span>
              <span className="ml-auto text-gray-500 dark:text-gray-400">
                {t('settings:scans.includedWithPro')}
              </span>
            </p>
          )}
          <p className="text-sm text-gray-500 dark:text-gray-400">
            <SupporterBadge className="mr-1" />
            {t('members:supporter.selfNote')}
          </p>
        </div>

        <div className="space-y-2">
          <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t('settings:pro.groups', pro.slots)}
          </h4>
          <SlotMeter used={pro.slots.used} total={pro.slots.total} />
          <div
            className="space-y-3 border-t border-gray-100 pt-3 dark:border-gray-700"
            data-testid="settings-pro-groups-intro"
          >
            {[
              t('settings:pro.groupsIntro', { total: pro.slots.total }),
              t('settings:pro.groupsIntroCooldown'),
            ].map(paragraph => (
              <p
                key={paragraph}
                className="text-sm text-gray-500 dark:text-gray-400"
              >
                {paragraph}
              </p>
            ))}
          </div>
          {!showEmpty && makeProAction && (
            <div className="pt-2">{makeProAction}</div>
          )}
          {loadingGroups ? (
            <div role="status" data-testid="settings-pro-loading">
              <div aria-hidden="true" className="loading-spinner w-5 h-5" />
              <span className="sr-only">{t('common:actions.loading')}</span>
            </div>
          ) : groupsFailed ? (
            <div className="space-y-2">
              <Alert type="error" message={t('errors:api.status.default')} />
              <Button
                variant="outline"
                size="sm"
                className="min-h-[44px]"
                onClick={onRetry}
                loading={retryingGroups}
              >
                {t('common:errors.retry')}
              </Button>
            </div>
          ) : proGroups.length === 0 ? (
            <div className="space-y-3" data-testid="settings-pro-empty">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                {t('settings:pro.empty')}
              </p>
              {makeProAction}
            </div>
          ) : (
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {proGroups.map(item => {
                const locked = isProSlotLocked(item.movableAt, now);
                const movableDate = locked
                  ? formatProMovableAt(item.movableAt, i18n.language, now)
                  : null;
                const lockedHintId = `${offlineHintId}-${item.groupId}`;
                return (
                  <li
                    key={item.groupId}
                    className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2"
                  >
                    <div className="min-w-[45%] flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 break-words">
                        {item.groupName}
                      </p>
                      {movableDate && (
                        <p
                          id={lockedHintId}
                          className="text-xs text-gray-500 dark:text-gray-400"
                        >
                          {t('settings:pro.movableOn', { date: movableDate })}
                        </p>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="ml-auto"
                      onClick={() => setConfirming(item)}
                      disabled={!isOnline || locked || !!busyGroupId}
                      loading={busyGroupId === item.groupId}
                      title={isOnline ? undefined : offlineHint}
                      aria-label={t('settings:pro.removeA11y', {
                        groupName: item.groupName,
                      })}
                      aria-describedby={
                        [
                          movableDate && lockedHintId,
                          !isOnline && offlineHintId,
                        ]
                          .filter(Boolean)
                          .join(' ') || undefined
                      }
                    >
                      {t('settings:pro.remove')}
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
          {!isOnline && (
            <p
              id={offlineHintId}
              className="text-xs text-gray-500 dark:text-gray-400"
            >
              {offlineHint}
            </p>
          )}
        </div>
      </div>

      <ConfirmationDialog
        isOpen={confirming !== null}
        onClose={() => setConfirming(null)}
        onConfirm={() => {
          if (confirming) onRemove(confirming.groupId);
          setConfirming(null);
        }}
        title={t('groups:pro.sheet.remove')}
        description={t('groups:pro.confirm.remove', {
          groupName: confirming?.groupName,
        })}
        confirmText={t('groups:pro.sheet.remove')}
        cancelText={t('common:actions.cancel')}
        variant="danger"
      />
    </Card>
  );
}

/** One segment per slot, filled per slot in use; the heading already says it. */
function SlotMeter({ used, total }: { used: number; total: number }) {
  if (total <= 0) return null;
  return (
    <div
      aria-hidden="true"
      className="flex gap-1.5 pt-1"
      data-testid="settings-pro-meter"
    >
      {Array.from({ length: total }, (_, index) => (
        <span
          key={index}
          data-slot={index < used ? 'used' : 'free'}
          className={`h-2 flex-1 rounded-full ${
            index < used
              ? 'bg-primary-600 dark:bg-primary-400'
              : 'bg-gray-200 dark:bg-gray-700'
          }`}
        />
      ))}
    </div>
  );
}
